<!--
  Copy a string, and say so.

  WHY THIS IS SHARED. `AdminAffiliate` hand-rolled a `copied` flag, a timer and a
  try/catch around `navigator.clipboard`, and the moment a second place needed the same
  thing there were going to be two of them drifting apart — one with a failure path and
  one without. This is that logic, once.

  THE FAILURE PATH IS THE INTERESTING PART. `navigator.clipboard.writeText` rejects on a
  page that is not secure-context or has lost focus, and swallowing that leaves a button
  that looks like it worked. It says so instead: a copy that did not happen must not
  render as one that did.
-->
<script lang="ts">
  interface Props {
    /** The text to put on the clipboard. */
    value: string;
    /** Button text before copying. Omit for an icon-only button. */
    label?: string;
    /** Native tooltip — default is the value itself, which is what a reader wants
     *  when the button shows a short label and copies a long URL. */
    title?: string;
    class?: string;
  }

  let { value, label = '', title = '', class: klass = '' }: Props = $props();

  type State = 'idle' | 'done' | 'failed';
  let state = $state<State>('idle');
  let timer: ReturnType<typeof setTimeout> | undefined;

  async function copy(): Promise<void> {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      state = 'done';
    } catch {
      state = 'failed';
    }
    clearTimeout(timer);
    // Long enough to read, short enough that a stale "Copied" never sits under a
    // second press.
    timer = setTimeout(() => (state = 'idle'), 1600);
  }
</script>

<button
  type="button"
  class="btn btn-ghost btn-xs gap-1 {klass}"
  title={title || value}
  disabled={!value}
  onclick={copy}
>
  {#if state === 'done'}
    <svg class="h-3.5 w-3.5 text-success" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
    <span class="text-success">Copied</span>
  {:else if state === 'failed'}
    <svg class="h-3.5 w-3.5 text-warning" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
    <span class="text-warning">Press ⌘C</span>
  {:else}
    <svg class="h-3.5 w-3.5 opacity-70" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
    {#if label}<span>{label}</span>{/if}
  {/if}
</button>
